import React, {useEffect, useMemo, useState} from "react";
import {useExpanded, useFilters, useGlobalFilter, usePagination, useRowSelect, useSortBy, useTable} from "react-table";
import {matchSorter} from "match-sorter";
import {Pagination, Tooltip} from "@mui/material";
import {ArrowDropDown, ArrowDropUp, InfoSharp} from "@mui/icons-material";
import Checkbox from "@mui/material/Checkbox";
import {debounce} from "lodash";
import {DndProvider, useDrag, useDrop} from 'react-dnd';
import {HTML5Backend} from 'react-dnd-html5-backend';
import {toast} from "react-toastify";
import {DND_ITEM_TYPE} from "../Utils/constants";

// Draggable Column Header Component
const DraggableColumnHeader = ({column, index, moveColumn}) => {
    const [{isDragging}, drag] = useDrag({
        type: DND_ITEM_TYPE,
        item: {index},
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    });

    const [, drop] = useDrop({
        accept: DND_ITEM_TYPE,
        hover(item) {
            if (item.index !== index) {
                moveColumn(item.index, index);
                item.index = index;
            }
        },
    });

    return (
        <th
            {...column.getHeaderProps(column.getSortByToggleProps())}
            ref={(node) => drag(drop(node))}
            style={{
                opacity: isDragging ? 0.5 : 1,
                cursor: 'move',
                background: "#f3f3f3"
            }}
        >
            <div>
                {column.render("Header")}
                <span>
                    {column.isSorted ? (
                        column.isSortedDesc ? (
                            <ArrowDropDown sx={{marginLeft: "5px"}}/>
                        ) : (
                            <ArrowDropUp sx={{marginLeft: "5px"}}/>
                        )
                    ) : (
                        ""
                    )}
                </span>
            </div>
        </th>
    );
};

// Define a default UI for filtering
function DefaultColumnFilter({column: {filterValue, setFilter}}) {
    return (
        <input
            value={filterValue || ""}
            onChange={(e) => {
                setFilter(e.target.value || undefined);
            }}
            placeholder={`Search`}
            style={{width: "100%"}}
        />
    );
}

function fuzzyTextFilterFn(rows, id, filterValue) {
    return matchSorter(rows, filterValue, {keys: [(row) => row.values[id]]});
}

fuzzyTextFilterFn.autoRemove = (val) => !val;

function filterGreaterThan(rows, id, filterValue) {
    return rows.filter((row) => {
        const rowValue = row.values[id];
        return rowValue >= filterValue;
    });
}

filterGreaterThan.autoRemove = (val) => typeof val !== "number";

const getCellColor = (data, column) => {
    if (data.isSingleOrder && !data.isChildOrder && !data.isParentOrder) {
        return "";
    } else {
        if (data.isParentOrder) {
            return "#5c76ff";
        }

        if (data.isChildOrder) {
            return "#cde0f9";
        }
    }

    if (column === "invoices.invoice_status" && data.invoice_status) {
        if (data.invoice_status === "Pending") {
            var currentDate = new Date();
            var deadline = new Date(data.invoice_deadline_date);

            var yellowDate = new Date(data.invoice_deadline_date);
            yellowDate.setDate(yellowDate.getDate() - 4);

            var orangeDate = new Date(data.invoice_deadline_date);
            orangeDate.setDate(orangeDate.getDate() - 2);

            if (currentDate >= deadline) {
                return "red";
            } else if (currentDate >= orangeDate) {
                return "orange";
            } else if (currentDate >= yellowDate) {
                return "yellow";
            }
        } else {
            return "green";
        }
    }
};

const getCellTextColor = (data, column) => {
    if (column === "invoices.invoice_status" && data.invoice_status) {
        if (data.invoice_status === "Pending") {
            var currentDate = new Date();
            var deadline = new Date(data.invoice_deadline_date);

            var yellowDate = new Date(data.invoice_deadline_date);
            yellowDate.setDate(yellowDate.getDate() - 4);

            var orangeDate = new Date(data.invoice_deadline_date);
            orangeDate.setDate(orangeDate.getDate() - 2);

            if (currentDate >= deadline) {
                return "white";
            } else if (currentDate >= orangeDate) {
                return "black";
            } else if (currentDate >= yellowDate) {
                return "black";
            }
        } else {
            return "white";
        }
    }
};

const getCellText = (data, cell) => {
    if (cell.column.column === "invoices.invoice_status" && data.invoice_status === 'Failed' && data?.error_message) {
        return (
            <div>
                {data.invoice_status} &nbsp;
                <Tooltip style={{maxWidth: 600}} title={data?.error_message} arrow placement='top'>
                    <InfoSharp/>
                </Tooltip>
            </div>
        );
    }
    return cell.render("Cell");
};

// Table component
const DnDGridElement = ({
                            columns: initialColumns,
                            data,
                            pageCount: controlledPageCount,
                            loading,
                            fetchData,
                            filters,
                            setFilters,
                            controlledPageIndex,
                            setControlledPageIndex,
                            rowsCount,
                            handleRowClick,
                            initialSortBy,
                            sortOrder,
                            hideSelection,
                            refreshGridData,
                            selectableCount,
                            handleRowSelection,
                            validateBeforeSelection,
                            saveColumnOrder
                        }) => {
    const [allRowsSelected, setAllRowsSelected] = React.useState(false);
    const [isRowsSelected, setIsRowsSelected] = React.useState(false);
    const [selectedRows, setSelectedRows] = React.useState({});
    const [columns, setColumns] = useState(initialColumns);

    handleRowSelection = handleRowSelection || (() => {
    });

    const moveColumn = async (fromIndex, toIndex) => {
        const newColumns = [...columns];
        const [movedColumn] = newColumns.splice(fromIndex, 1);
        newColumns.splice(toIndex, 0, movedColumn);
        setColumns(newColumns);

        // Prepare column order data to send to backend
        const columnOrder = newColumns.map(col => col.column || col.id);

        try {
            // Send column order to backend
            await saveColumnOrder(columnOrder);
            toast.success("Column order saved successfully");
        } catch (error) {
            toast.error("Failed to save column order");
        }
    };

    const toggleSelectAllRows = (e) => {
        const checked = e.target.checked;
        setAllRowsSelected(checked);
        if (checked) {
            setSelectedRows({
                ...selectedRows,
                [controlledPageIndex]: data.map((r) => r[columns[0].accessor]),
            });
        } else if (selectedRows[controlledPageIndex]) {
            setSelectedRows({...selectedRows, [controlledPageIndex]: []});
            setIsRowsSelected(false);
        }
    };

    useEffect(() => {
        const _selectedRows = selectedRows[controlledPageIndex] || [];
        setAllRowsSelected(data.length === _selectedRows.length);
        setIsRowsSelected(_selectedRows.length > 0);
    }, [data]);

    const toggleRowSelect = (id, row) => {
        let _selectedRows = selectedRows[controlledPageIndex]
            ? [...selectedRows[controlledPageIndex]]
            : [];

        let selectedCount = _selectedRows.length;
        let checked = false;

        if (_selectedRows.includes(id)) {
            _selectedRows = _selectedRows.filter((rId) => rId !== id);
            checked = false;
        } else {
            if (selectableCount === 1) {
                _selectedRows = [id];
            } else {
                if (selectedCount < selectableCount) {
                    _selectedRows.push(id);
                }
            }
            checked = true;
        }

        if (validateBeforeSelection) {
            if (validateBeforeSelection(row.original, checked)) return;
        }

        setAllRowsSelected(_selectedRows.length === data.length);
        setSelectedRows({
            ...selectedRows,
            [controlledPageIndex]: _selectedRows,
        });
        setIsRowsSelected(_selectedRows.length > 0);
        handleRowSelection(checked, id);
    };

    const filterTypes = React.useMemo(
        () => ({
            fuzzyText: fuzzyTextFilterFn,
            text: (rows, id, filterValue) => {
                return rows.filter((row) => {
                    const rowValue = row.values[id];
                    return rowValue !== undefined
                        ? String(rowValue)
                            .toLowerCase()
                            .startsWith(String(filterValue).toLowerCase())
                        : true;
                });
            },
        }),
        []
    );

    const defaultColumn = React.useMemo(
        () => ({
            Filter: DefaultColumnFilter,
        }),
        []
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        state,
        page,
        pageCount,
        gotoPage,
        setPageSize,
        state: {pageIndex, pageSize, sortBy},
    } = useTable(
        {
            columns,
            data,
            defaultColumn,
            filterTypes,
            initialState: {
                pageIndex: controlledPageIndex,
                pageSize: 10,
                filters,
                sortBy: [
                    {
                        id: initialSortBy,
                        desc: sortOrder === "desc",
                    },
                ],
            },
            manualPagination: true,
            pageCount: controlledPageCount,
            manualFilters: true,
            manualSortBy: true,
        },
        useFilters,
        useGlobalFilter,
        useSortBy,
        useExpanded,
        usePagination,
        useRowSelect
    );

    const hiddenColumns = ["_selector"];
    hiddenColumns.forEach((columnId) => {
        const index = columns.findIndex((column) => column.id === columnId);
        if (index !== -1) {
            columns.splice(index, 1);
        }
    });

    const debounceFetchData = useMemo(() => debounce(fetchData, 1100), []);

    React.useEffect(() => {
        const timer = setTimeout(() => {
            setFilters(state.filters);
            setControlledPageIndex(pageIndex);
        }, 300);

        return () => clearTimeout(timer);
    }, [state.filters]);

    React.useEffect(() => {
        refreshGridData && fetchData &&
        debounceFetchData({
            pageIndex,
            pageSize,
            sortBy,
            filters,
            columns,
        });
    }, [refreshGridData]);

    React.useEffect(() => {
        fetchData &&
        debounceFetchData({
            pageIndex,
            pageSize,
            sortBy,
            filters,
            columns,
        });
    }, [
        fetchData,
        pageIndex,
        pageSize,
        sortBy,
        setFilters,
        columns,
        filters
    ]);

    return (
        <DndProvider backend={HTML5Backend}>
            <div className="react-table-container">
                <div style={{display: "flex", justifyContent: "space-between", marginBottom: "10px"}}>
                    <div>
                        <span>Total: {rowsCount}</span>
                    </div>
                    <div className="pagination">
                        <Pagination
                            color="primary"
                            count={pageCount}
                            page={pageIndex + 1}
                            onChange={(_event, value) => {
                                gotoPage(value - 1);
                            }}
                        />
                        <span>
                            | Go to page:{" "}
                            <input
                                type="number"
                                defaultValue={pageIndex + 1}
                                onChange={(e) => {
                                    const page = e.target.value ? Number(e.target.value) - 1 : 0;
                                    gotoPage(page);
                                }}
                                style={{width: "100px"}}
                            />
                        </span>{" "}
                        <select
                            style={{minHeight: "25px", marginLeft: "5px"}}
                            value={pageSize}
                            onChange={(e) => {
                                setPageSize(Number(e.target.value));
                            }}
                        >
                            {[10, 20, 30, 40, 50].map((pageSize) => (
                                <option key={pageSize} value={pageSize}>
                                    Show {pageSize}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
                <div className="react-table-wrapper">
                    <table {...getTableProps()}>
                        <thead>
                        {headerGroups.map((headerGroup) => (
                            <tr {...headerGroup.getHeaderGroupProps()}>
                                {!hideSelection && (
                                    <th style={{minWidth: "0", paddingLeft: "1rem", paddingRight: "1rem"}}>
                                        {selectableCount > 1 && (
                                            <Checkbox
                                                indeterminate={!allRowsSelected && isRowsSelected}
                                                checked={allRowsSelected}
                                                onClick={toggleSelectAllRows}
                                            />
                                        )}
                                    </th>
                                )}
                                {headerGroup.headers.map((column, index) => (
                                    <DraggableColumnHeader
                                        key={column.id}
                                        column={column}
                                        index={index}
                                        moveColumn={moveColumn}
                                    />
                                ))}
                            </tr>
                        ))}
                        {headerGroups.map((headerGroup) => (
                            <tr {...headerGroup.getHeaderGroupProps()}>
                                {!hideSelection && (
                                    <th style={{minWidth: "0"}}></th>
                                )}
                                {headerGroup.headers.map((column) => (
                                    <th {...column.getHeaderProps()}>
                                        <div>
                                            {column.canFilter ? column.render("Filter") : null}
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        ))}
                        </thead>
                        {loading ? (
                            <tbody {...getTableBodyProps()}>
                            <tr>
                                <td colSpan="10000">Loading...</td>
                            </tr>
                            </tbody>
                        ) : (
                            <tbody {...getTableBodyProps()}>
                            {!page.length ? (
                                <tr>
                                    <td colSpan={100} style={{textAlign: "center"}}>
                                        No record found
                                    </td>
                                </tr>
                            ) : (
                                page.map((row) => {
                                    prepareRow(row);
                                    return (
                                        <React.Fragment key={row.id}>
                                            <tr {...row.getRowProps()}>
                                                {!hideSelection && (
                                                    <td style={{minWidth: "0"}}>
                                                        {!row?.original?.disable_row_selection && (
                                                            <Checkbox
                                                                checked={
                                                                    allRowsSelected ||
                                                                    !!selectedRows[controlledPageIndex]?.includes(
                                                                        row.allCells[0].value
                                                                    )
                                                                }
                                                                disabled={
                                                                    selectableCount === 1 &&
                                                                    selectedRows[controlledPageIndex]?.length > 0 &&
                                                                    !selectedRows[controlledPageIndex]?.includes(
                                                                        row.allCells[0].value
                                                                    )
                                                                }
                                                                onClick={() =>
                                                                    toggleRowSelect(row.allCells[0].value, row)
                                                                }
                                                            />
                                                        )}
                                                    </td>
                                                )}
                                                {row.cells.map((cell) => (
                                                    <td
                                                        {...cell.getCellProps()}
                                                        onClick={() => handleRowClick(row)}
                                                        style={{
                                                            backgroundColor: getCellColor(row.original, cell.column.column),
                                                            color: getCellTextColor(row.original, cell.column.column),
                                                        }}
                                                    >
                                                        {getCellText(row.original, cell)}
                                                    </td>
                                                ))}
                                            </tr>
                                            {row.isExpanded ? (
                                                <tr>
                                                    {row.cells.map((cell) => (
                                                        <td
                                                            {...cell.getCellProps()}
                                                            onClick={() => handleRowClick(row)}
                                                            style={{
                                                                backgroundColor: getCellColor(row.original, cell.column.column),
                                                                color: getCellTextColor(row.original, cell.column.column),
                                                            }}
                                                        >
                                                            {cell.render("Cell")}
                                                        </td>
                                                    ))}
                                                </tr>
                                            ) : null}
                                        </React.Fragment>
                                    );
                                })
                            )}
                            </tbody>
                        )}
                    </table>
                </div>
            </div>
        </DndProvider>
    );
};

export default DnDGridElement;